import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/primitives';
import { useAuth, type AuthUser } from '@/lib/auth';
import { useScope, ROLE_OPTIONS, type AppRole } from '@/lib/scope';

interface Props {
  user: AuthUser;
  compact?: boolean;
}

export function UserMenu({ user, compact }: Props) {
  const { signOut } = useAuth();
  const { role, setRole } = useScope();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={user.displayName || user.email}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: '0 6px 0 8px',
          background: 'transparent',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 9999,
            background: 'var(--bx-primary)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {(user.displayName || user.email).slice(0, 1).toUpperCase()}
        </div>
        {!compact && (
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--bx-text-dark)',
              whiteSpace: 'nowrap',
            }}
          >
            {user.displayName || user.email.split('@')[0]}
          </span>
        )}
        <Icon name="chevron-down" size={10} style={{ color: 'var(--bx-text-muted)' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 220,
            background: '#fff',
            border: '1px solid var(--bx-border-muted)',
            borderRadius: 'var(--bx-radius-md)',
            boxShadow: 'var(--bx-shadow-pop)',
            zIndex: 60,
            padding: 6,
          }}
        >
          <div
            style={{
              padding: '8px 10px',
              borderBottom: '1px solid var(--bx-border-muted)',
              marginBottom: 4,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bx-text-dark)' }}>
              {user.displayName || user.email}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--bx-text-muted)' }}>{user.email}</div>
          </div>
          {compact && (
            <div style={{ padding: '6px 10px' }}>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--bx-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                Vai trò
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {ROLE_OPTIONS.map((r) => {
                  const active = role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRole(r.value as AppRole)}
                      style={{
                        padding: '6px 8px',
                        border:
                          '1px solid ' +
                          (active ? 'var(--bx-primary)' : 'var(--bx-border)'),
                        background: active ? '#eef2ff' : '#fff',
                        color: active ? 'var(--bx-primary)' : 'var(--bx-text-dark)',
                        borderRadius: 8,
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => signOut()}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 10px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: 6,
              color: 'var(--bx-text-dark)',
              fontSize: 12.5,
              textAlign: 'left',
            }}
          >
            <Icon name="logout" size={12} style={{ color: 'var(--bx-text-muted)' }} />
            Đăng xuất
          </button>
        </div>
      )}
    </div>
  );
}
